import { toast } from "sonner";

import { Button } from "~/components/ui/button";
import { useUpgradeFlow } from "~/hooks/mutations/use-upgrade-flow";
import { useBillingNudge } from "~/hooks/queries/use-billing-nudge";
import { useBillingUpgradeContext } from "~/hooks/queries/use-billing-upgrade-context";

import { PaywallContactDialog } from "~/components/billing/paywall-contact-dialog";

interface BillingAhaNudgeDisplayProps {
    className?: string;
    onUpgrade?: () => void;
    onDismiss?: () => void;
}

// AIDEV-NOTE: Pure presentation component — no hooks. Use BillingAhaNudge for the hooked version.
export function BillingAhaNudgeDisplay({
    className,
    onUpgrade,
    onDismiss,
}: BillingAhaNudgeDisplayProps) {
    return (
        <div
            className={`rounded-xl border border-brand-forest-200 bg-brand-cream-50 p-4 ${
                className ?? ""
            }`}
        >
            <p className="text-sm font-semibold text-brand-forest-900">
                Your journey is just beginning
            </p>
            <p className="mt-1 text-sm text-brand-forest-700">
                You&apos;ve completed your first full session analysis. Upgrade
                to keep exploring with full support.
            </p>
            <div className="mt-3 flex flex-wrap items-center gap-3">
                <Button size="sm" onClick={onUpgrade}>
                    Keep the Momentum
                </Button>
                <Button size="sm" variant="ghost" onClick={onDismiss}>
                    Not right now
                </Button>
                <PaywallContactDialog surface="aha_nudge" variant="forest" />
            </div>
        </div>
    );
}

interface BillingAhaNudgeProps {
    className?: string;
}

export function BillingAhaNudge({ className }: BillingAhaNudgeProps) {
    const nudge = useBillingNudge({ key: "aha_first_session_completed" });
    const { data: upgradeContext } = useBillingUpgradeContext();
    const upgradeFlow = useUpgradeFlow();

    // AIDEV-NOTE: Upsell nudge must only target unpaid accounts (starter or trial). Paying
    // integration/growth practitioners hit usedUnits===1 on every cycle rollover, and nudge
    // dismissals reset per cycle — without this gate they'd see "upgrade" monthly despite paying.
    const policy = upgradeContext?.policy ?? null;
    const isUnpaidTier =
        policy !== null && (policy.tierName === "starter" || policy.isTrial);

    if (nudge.isDismissed || !isUnpaidTier) {
        return null;
    }

    return (
        <BillingAhaNudgeDisplay
            className={className}
            onUpgrade={async () => {
                try {
                    await upgradeFlow.mutateAsync({
                        source: "aha_first_session_completed",
                        nudgeKey: "aha_first_session_completed",
                    });
                } catch (error) {
                    toast.error(
                        (error as Error).message || "Unable to start checkout.",
                    );
                }
            }}
            onDismiss={() => {
                nudge.dismiss().catch(() => undefined);
            }}
        />
    );
}
